import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Calendar as CalendarComponent } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Calendar, MapPin, Users, Clock, ArrowLeft, Link2, MapPinned, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import SearchableSelect from './SearchableSelect';
import PhotoUploader from './PhotoUploader';
import OrganiserApplyForm from './OrganiserApplyForm';
import { ALL_STATES, getDistrictsForState } from './data/indianStatesDistricts';
import { createEvent } from '../apis/ride-24';

const apiError = (error, fallback) =>
  error.response?.data?.detail || error.response?.data?.message || fallback;

const CreateEvent = ({ user, onUserRefresh }) => {
  const navigate = useNavigate();

  // Gate: only verified organisers can create rides. Show apply UI instead.
  if (user.organiser_status !== 'approved' && !user.is_verified) {
    return <CreateEventGate user={user} navigate={navigate} onUserRefresh={onUserRefresh} />;
  }

  return <CreateEventForm user={user} navigate={navigate} />;
};

const CreateEventGate = ({ user, navigate, onUserRefresh }) => {
  const status = user.organiser_status;
  return (
    <div className="min-h-screen bg-[#09090B]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Button
          onClick={() => navigate('/ride-24')}
          variant="ghost"
          className="mb-6 -ml-2 text-gray-300 hover:text-white hover:bg-white/5"
          data-testid="back-to-dashboard-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        <div className="mb-8">
          <div className="text-xs uppercase tracking-[0.3em] text-red-500 font-heading mb-2">
            Verified organiser
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold uppercase tracking-tight text-white leading-none">
            Be a verified Organiser
          </h1>
          <p className="text-zinc-400 mt-3">
            Only verified riders can create rides. Complete your application below.
          </p>
        </div>

        {status === 'pending' ? (
          <Card className="bg-zinc-950 border border-zinc-900 text-gray-200" data-testid="gate-pending">
            <CardContent className="py-10 text-center">
              <Clock className="w-8 h-8 text-yellow-500 mx-auto mb-3" />
              <h2 className="font-heading text-2xl uppercase tracking-tight text-white mb-2">
                Application under review
              </h2>
              <p className="text-zinc-400">
                Our admin is reviewing your application. You&apos;ll be able to create rides once approved.
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card className="bg-zinc-950 border border-zinc-900 text-gray-200" data-testid="gate-apply">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2 font-heading uppercase tracking-tight">
                <ShieldCheck className="w-5 h-5 text-red-500" />
                Organiser application
              </CardTitle>
              <CardDescription className="text-gray-400">
                Provide your photo, ID document, and ID details.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <OrganiserApplyForm
                defaultName={user.name}
                onSubmitted={() => onUserRefresh && onUserRefresh()}
              />
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

const CreateEventForm = ({ user, navigate }) => {
  const [loading, setLoading] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    date: null, // Date object
    time: '',
    start_point: '',
    end_point: '',
    google_maps_link: '',
    state: null,
    district: null,
    difficulty_level: '',
    bike_type: '',
    max_participants: '',
    photos: [],
    gender_restriction: 'anyone'
  });

  const difficultyOptions = [
    { value: 'easy', label: 'Easy', description: 'Suitable for new riders' },
    { value: 'moderate', label: 'Moderate', description: 'Some riding experience required' },
    { value: 'challenging', label: 'Challenging', description: 'For experienced motorcyclists' },
    { value: 'expert', label: 'Expert', description: 'Advanced riders only' }
  ];

  const bikeTypeOptions = [
    { value: 'any', label: 'Any' },
    { value: 'cruiser', label: 'Cruiser' },
    { value: 'sport', label: 'Sport / Supersport' },
    { value: 'touring', label: 'Touring' },
    { value: 'adventure', label: 'Adventure / ADV' },
    { value: 'dirt', label: 'Dirt / Off-road' },
    { value: 'naked', label: 'Naked / Streetfighter' },
    { value: 'cafe_racer', label: 'Cafe Racer' },
    { value: 'scooter', label: 'Scooter' }
  ];

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSelectChange = (name, value) => {
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleDateSelect = (date) => {
    setFormData({ ...formData, date });
    setDatePickerOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Combine date and time into a local Date, then ISO
      const [hh, mm] = formData.time.split(':');
      const eventDateTime = new Date(formData.date);
      eventDateTime.setHours(parseInt(hh, 10), parseInt(mm, 10), 0, 0);

      const eventData = {
        title: formData.title,
        description: formData.description,
        date: eventDateTime.toISOString(),
        start_point: formData.start_point,
        end_point: formData.end_point,
        google_maps_link: formData.google_maps_link.trim() || null,
        state: formData.state,
        district: formData.district,
        difficulty_level: formData.difficulty_level,
        bike_type: formData.bike_type,
        max_participants: parseInt(formData.max_participants),
        photos: formData.photos,
        gender_restriction: formData.gender_restriction,
      };

      const response = await createEvent(eventData);

      toast.success('Event created successfully!');
      navigate(`/ride-24/events/${response.data.id}`);
    } catch (error) {
      console.error('Error creating event:', error);
      toast.error(apiError(error, 'Failed to create event'));
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = () => {
    return (
      formData.title.trim() &&
      formData.description.trim() &&
      formData.date &&
      formData.time &&
      formData.start_point.trim() &&
      formData.end_point.trim() &&
      formData.state &&
      formData.district &&
      formData.difficulty_level &&
      formData.bike_type &&
      formData.max_participants &&
      parseInt(formData.max_participants) > 0
    );
  };

  const stateOptions = ALL_STATES.map((s) => ({ value: s, label: s }));
  const districtOptions = getDistrictsForState(formData.state).map((d) => ({ value: d, label: d }));

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const inputDark =
    'bg-gray-950 border-gray-700 text-gray-100 placeholder:text-gray-500 focus-visible:ring-red-600 focus-visible:ring-offset-0';

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center mb-8">
          <Button
            onClick={() => navigate('/ride-24')}
            variant="ghost"
            className="mr-4 p-2 text-gray-300 hover:text-white hover:bg-white/5"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>

          <div>
            <h1 className="text-3xl font-bold text-white" data-testid="create-event-title">
              Create New Ride
            </h1>
            <p className="text-gray-400 mt-2">
              Organize a motorcycle ride for the community
            </p>
          </div>
        </div>

        {/* Form */}
        <Card className="bg-gray-900/80 border border-gray-800 shadow-xl text-gray-200">
          <CardHeader>
            <CardTitle className="text-white">Ride Details</CardTitle>
            <CardDescription className="text-gray-400">
              Fill in the information about your motorcycle ride
            </CardDescription>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Information */}
              <div className="grid gap-6 md:grid-cols-2">
                <div className="md:col-span-2">
                  <Label htmlFor="title" className="text-gray-200">Ride Title *</Label>
                  <Input
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="e.g., Sunday Morning Coastal Run"
                    className={inputDark}
                    required
                    data-testid="event-title-input"
                  />
                </div>

                <div className="md:col-span-2">
                  <Label htmlFor="description" className="text-gray-200">Description *</Label>
                  <Textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Describe the route, pace, what to bring, fuel stops..."
                    rows={4}
                    className={inputDark}
                    required
                    data-testid="event-description-input"
                  />
                </div>
              </div>

              {/* Date and Time */}
              <div className="grid gap-6 md:grid-cols-2">
                <div>
                  <Label className="text-gray-200">Date *</Label>
                  <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        className={`w-full justify-start text-left font-normal ${inputDark} hover:bg-gray-900 hover:text-gray-100 ${
                          !formData.date && 'text-gray-500'
                        }`}
                        data-testid="event-date-input"
                      >
                        <Calendar className="mr-2 h-4 w-4 text-gray-400" />
                        {formData.date ? format(formData.date, 'PPP') : <span>Pick a date</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      className="w-auto p-0 bg-gray-900 border border-gray-800 text-gray-200"
                      align="start"
                    >
                      <CalendarComponent
                        mode="single"
                        selected={formData.date}
                        onSelect={handleDateSelect}
                        disabled={(d) => d < today}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div>
                  <Label htmlFor="time" className="text-gray-200">Time *</Label>
                  <div className="relative">
                    <Clock className="absolute left-3 top-3 h-4 w-4 text-gray-400 z-10 pointer-events-none" />
                    <Input
                      id="time"
                      name="time"
                      type="time"
                      value={formData.time}
                      onChange={handleInputChange}
                      className={`pl-10 ${inputDark} [color-scheme:dark]`}
                      required
                      data-testid="event-time-input"
                    />
                  </div>
                </div>
              </div>

              {/* Route Information */}
              <div className="grid gap-6 md:grid-cols-2">
                <div>
                  <Label htmlFor="start_point" className="text-gray-200">Start Point *</Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-3 h-4 w-4 text-red-500" />
                    <Input
                      id="start_point"
                      name="start_point"
                      value={formData.start_point}
                      onChange={handleInputChange}
                      placeholder="e.g., Central Park Main Entrance"
                      className={`pl-10 ${inputDark}`}
                      required
                      data-testid="event-start-point-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="end_point" className="text-gray-200">End Point *</Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-3 h-4 w-4 text-red-500" />
                    <Input
                      id="end_point"
                      name="end_point"
                      value={formData.end_point}
                      onChange={handleInputChange}
                      placeholder="e.g., Brooklyn Bridge"
                      className={`pl-10 ${inputDark}`}
                      required
                      data-testid="event-end-point-input"
                    />
                  </div>
                </div>
              </div>

              {/* Google Maps Link */}
              <div>
                <Label htmlFor="google_maps_link" className="text-gray-200">
                  Google Maps Route Link (optional)
                </Label>
                <div className="relative">
                  <Link2 className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    id="google_maps_link"
                    name="google_maps_link"
                    type="url"
                    value={formData.google_maps_link}
                    onChange={handleInputChange}
                    placeholder="https://maps.google.com/?..."
                    className={`pl-10 ${inputDark}`}
                    data-testid="event-google-maps-link-input"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Share a Google Maps link to the full route so riders can preview it.
                </p>
              </div>

              {/* State + District (Indian) */}
              <div className="grid gap-6 md:grid-cols-2">
                <div>
                  <Label className="text-gray-200">State *</Label>
                  <div className="relative">
                    <MapPinned className="absolute left-3 top-3 h-4 w-4 text-red-500 z-10 pointer-events-none" />
                    <SearchableSelect
                      value={formData.state}
                      onChange={(val) => setFormData({ ...formData, state: val, district: null })}
                      options={stateOptions}
                      placeholder="Select state / UT"
                      emptyMessage="No matching state."
                      testId="state-select"
                      className="pl-10"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-gray-200">District *</Label>
                  <div className="relative">
                    <MapPinned className="absolute left-3 top-3 h-4 w-4 text-red-500 z-10 pointer-events-none" />
                    <SearchableSelect
                      value={formData.district}
                      onChange={(val) => setFormData({ ...formData, district: val })}
                      options={districtOptions}
                      placeholder={formData.state ? 'Select district' : 'Pick a state first'}
                      emptyMessage="No matching district."
                      disabled={!formData.state}
                      testId="district-select"
                      className="pl-10"
                    />
                  </div>
                </div>
              </div>

              {/* Photos */}
              <div>
                <Label className="text-gray-200">Ride photos</Label>
                <PhotoUploader
                  photos={formData.photos}
                  onChange={(photos) => setFormData({ ...formData, photos })}
                />
              </div>

              {/* Gender restriction */}
              <div>
                <Label className="text-gray-200">Who can join *</Label>
                <div className="grid grid-cols-3 gap-2" data-testid="gender-restriction-toggle">
                  {[
                    { value: 'anyone', label: 'Anyone', hint: 'Open to all riders' },
                    { value: 'male_only', label: 'Male only', hint: 'Male riders only' },
                    { value: 'female_only', label: 'Female only', hint: 'Female riders only' },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, gender_restriction: opt.value })}
                      className={`p-3 rounded-md border-2 text-left transition-colors ${
                        formData.gender_restriction === opt.value
                          ? 'border-red-600 bg-red-950/30 text-white'
                          : 'border-gray-700 bg-gray-950 text-gray-300 hover:border-gray-600'
                      }`}
                      data-testid={`gender-restriction-${opt.value}`}
                    >
                      <div className="font-medium">{opt.label}</div>
                      <div className="text-xs text-gray-400 mt-0.5">{opt.hint}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Event Settings */}
              <div className="grid gap-6 md:grid-cols-3">
                <div>
                  <Label className="text-gray-200">Difficulty Level *</Label>
                  <Select
                    value={formData.difficulty_level}
                    onValueChange={(value) => handleSelectChange('difficulty_level', value)}
                  >
                    <SelectTrigger className={inputDark} data-testid="difficulty-select">
                      <SelectValue placeholder="Select difficulty" />
                    </SelectTrigger>
                    <SelectContent className="z-50 bg-gray-900 border border-gray-800 text-gray-200">
                      {difficultyOptions.map((option) => (
                        <SelectItem
                          key={option.value}
                          value={option.value}
                          data-testid={`difficulty-option-${option.value}`}
                          className="focus:bg-red-600 focus:text-white"
                        >
                          <div>
                            <div className="font-medium">{option.label}</div>
                            <div className="text-sm text-gray-500">{option.description}</div>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-gray-200">Ride Type *</Label>
                  <Select
                    value={formData.bike_type}
                    onValueChange={(value) => handleSelectChange('bike_type', value)}
                  >
                    <SelectTrigger className={inputDark} data-testid="bike-type-select">
                      <SelectValue placeholder="Select ride type" />
                    </SelectTrigger>
                    <SelectContent className="z-50 bg-gray-900 border border-gray-800 text-gray-200">
                      {bikeTypeOptions.map((option) => (
                        <SelectItem
                          key={option.value}
                          value={option.value}
                          data-testid={`bike-type-option-${option.value}`}
                          className="focus:bg-red-600 focus:text-white"
                        >
                          <div className="flex items-center">
                            <i className="fa-solid fa-motorcycle mr-2 not-italic" aria-hidden="true"></i>
                            {option.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="max_participants" className="text-gray-200">Max Participants *</Label>
                  <div className="relative">
                    <Users className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      id="max_participants"
                      name="max_participants"
                      type="number"
                      value={formData.max_participants}
                      onChange={handleInputChange}
                      placeholder="e.g., 20"
                      min="1"
                      max="100"
                      className={`pl-10 ${inputDark}`}
                      required
                      data-testid="max-participants-input"
                    />
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex justify-end space-x-4 pt-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/ride-24')}
                  className="border-gray-700 bg-transparent text-gray-200 hover:bg-gray-800 hover:text-white"
                  data-testid="cancel-button"
                >
                  Cancel
                </Button>

                <Button
                  type="submit"
                  disabled={!isFormValid() || loading}
                  className="btn-hover bg-red-600 hover:bg-red-700 text-white border-0 disabled:opacity-50"
                  data-testid="create-event-submit-button"
                >
                  {loading ? 'Creating...' : 'Create Ride'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CreateEvent;
